'use client';

import React, { useState } from 'react';
import Link from 'next/link';

interface FormData {
  fullName: string;
  company: string;
  email: string;
  phone: string;
  country: string;
  shipmentType: string;
}

const initialForm: FormData = {
  fullName: '',
  company: '',
  email: '',
  phone: '',
  country: '',
  shipmentType: '',
};

export default function PortalRegistrationPage() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formStartedAt] = useState(() => Date.now());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          formType: 'portal-registration',
          website: '',
          formStartedAt,
        }),
      });
      const responseText = await res.text();
      let data: { error?: string } = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error(responseText || 'Empty response from mail server.');
      }
      if (!res.ok) {
        throw new Error(data.error || responseText || 'Submission failed');
      }
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(160deg, #071220 0%, #0F2340 50%, #1A3558 100%)' }}
    >
      {/* Top nav bar */}
      <div className="w-full px-6 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <Link href="/home-page" className="flex items-center gap-2 group">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(126,200,220,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          <span className="text-sm font-body" style={{ color: 'rgba(126,200,220,0.8)' }}>Back to Home</span>
        </Link>
        <Link href="/home-page" className="flex items-center gap-2">
          <span className="text-sm font-bold font-body tracking-widest uppercase" style={{ color: '#FFFFFF', letterSpacing: '0.08em' }}>
            WORLD TRANSPORT<br />OVERSEAS
          </span>
        </Link>
      </div>

      {/* Hero */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left — Info */}
            <div>
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-6" style={{ borderColor: 'rgba(126,200,220,0.4)', background: 'rgba(126,200,220,0.1)' }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#7EC8DC' }} />
                <span className="text-xs font-medium font-body tracking-widest uppercase" style={{ color: '#7EC8DC' }}>
                  MY WTO Portal
                </span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-5" style={{ color: '#FFFFFF' }}>
                Register for<br />
                <span style={{ color: '#7EC8DC' }}>Portal Access</span>
              </h1>

              <p className="text-base font-body font-light leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.65)' }}>
                MY WTO is your personal online dashboard — track shipments live, download documents, book freight, and communicate with your WTO team. Fill in the form and we&apos;ll set up your credentials within <span className="font-semibold" style={{ color: '#FFFFFF' }}>1 business day</span>.
              </p>

              {/* Benefits */}
              <div className="space-y-3">
                {[
                  { icon: 'M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7', label: 'Real-time shipment tracking, 24/7' },
                  { icon: 'M9 12h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z', label: 'Instant access to all your documents' },
                  { icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', label: 'Create and manage freight bookings' },
                  { icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0 1 18 14.158V11a6.002 6.002 0 0 0-4-5.659V5a2 2 0 0 0-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9', label: 'Automated shipment milestone alerts' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(42,143,171,0.18)', border: '1px solid rgba(42,143,171,0.3)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2A8FAB" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                        <path d={item.icon} />
                      </svg>
                    </div>
                    <span className="text-sm font-body" style={{ color: 'rgba(255,255,255,0.75)' }}>{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(91,191,160,0.08)', border: '1px solid rgba(91,191,160,0.2)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5BBFA0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <p className="text-xs font-body leading-relaxed" style={{ color: '#A8C8D8' }}>
                  Portal access is available exclusively to registered WTO clients. Your data is handled securely and confidentially.
                </p>
              </div>
            </div>

            {/* Right — Form card */}
            <div
              className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(7,18,32,0.9)', border: '1px solid rgba(42,143,171,0.3)', boxShadow: '0 8px 48px rgba(0,0,0,0.4)' }}
            >
              {/* Card header */}
              <div className="px-7 py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(27,107,138,0.12)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(42,143,171,0.2)', border: '1px solid rgba(42,143,171,0.35)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7EC8DC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="19" y1="8" x2="19" y2="14" />
                      <line x1="22" y1="11" x2="16" y2="11" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold font-body text-base leading-tight" style={{ color: '#FFFFFF' }}>Request Portal Access</p>
                    <p className="text-xs font-body" style={{ color: '#7A9AB8' }}>Credentials delivered within 1 business day</p>
                  </div>
                </div>
              </div>

              <div className="p-7">
                {submitted ? (
                  /* Success state */
                  <div className="flex flex-col items-center justify-center py-10 text-center gap-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(91,191,160,0.15)', border: '1px solid rgba(91,191,160,0.35)' }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5BBFA0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold font-body text-xl mb-2" style={{ color: '#FFFFFF' }}>Request Received!</h3>
                      <p className="font-body text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>
                        Our team will review your request and send your MY WTO credentials within{' '}
                        <span className="font-semibold" style={{ color: '#FFFFFF' }}>1 business day</span>.
                      </p>
                    </div>
                    <div className="w-full mt-2 px-4 py-3 rounded-xl text-center" style={{ background: 'rgba(126,200,220,0.08)', border: '1px solid rgba(126,200,220,0.2)' }}>
                      <p className="text-xs font-body" style={{ color: '#A8C8D8' }}>
                        Check your inbox at <span className="font-semibold" style={{ color: '#7EC8DC' }}>{form.email}</span> for your login credentials.
                      </p>
                    </div>
                    <button
                      onClick={() => { setSubmitted(false); setForm(initialForm); }}
                      className="mt-2 text-sm font-body underline underline-offset-2 transition-colors duration-200"
                      style={{ color: '#7EC8DC' }}
                    >
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    {/* Row 1: Full Name + Company */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <label className="font-body font-semibold uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>
                          Full Name <span style={{ color: '#2A8FAB' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={form.fullName}
                          onChange={handleChange}
                          required
                          placeholder="Jane Smith"
                          className="rounded-xl px-4 py-2.5 text-sm font-body outline-none transition-all duration-200"
                          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; e.target.style.background = 'rgba(255,255,255,0.09)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.background = 'rgba(255,255,255,0.07)'; }}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="font-body font-semibold uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>
                          Company <span style={{ color: '#2A8FAB' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={form.company}
                          onChange={handleChange}
                          required
                          placeholder="Acme Ltd."
                          className="rounded-xl px-4 py-2.5 text-sm font-body outline-none transition-all duration-200"
                          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; e.target.style.background = 'rgba(255,255,255,0.09)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.background = 'rgba(255,255,255,0.07)'; }}
                        />
                      </div>
                    </div>

                    {/* Business Email */}
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body font-semibold uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>
                        Business Email <span style={{ color: '#2A8FAB' }}>*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="jane@company.com"
                        className="rounded-xl px-4 py-2.5 text-sm font-body outline-none transition-all duration-200"
                        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; e.target.style.background = 'rgba(255,255,255,0.09)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.background = 'rgba(255,255,255,0.07)'; }}
                      />
                    </div>

                    {/* Row 3: Phone + Country */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <label className="font-body font-semibold uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>
                          Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="+1 555 000"
                          className="rounded-xl px-4 py-2.5 text-sm font-body outline-none transition-all duration-200"
                          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; e.target.style.background = 'rgba(255,255,255,0.09)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.background = 'rgba(255,255,255,0.07)'; }}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="font-body font-semibold uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>
                          Country <span style={{ color: '#2A8FAB' }}>*</span>
                        </label>
                        <select
                          name="country"
                          value={form.country}
                          onChange={handleChange}
                          required
                          className="rounded-xl px-4 py-2.5 text-sm font-body outline-none transition-all duration-200 appearance-none"
                          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: form.country ? '#FFFFFF' : '#7A9AB8' }}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                        >
                          <option value="" disabled style={{ background: '#0E1F38' }}>Select country</option>
                          <option value="bg" style={{ background: '#0E1F38' }}>Bulgaria</option>
                          <option value="de" style={{ background: '#0E1F38' }}>Germany</option>
                          <option value="nl" style={{ background: '#0E1F38' }}>Netherlands</option>
                          <option value="gb" style={{ background: '#0E1F38' }}>United Kingdom</option>
                          <option value="us" style={{ background: '#0E1F38' }}>United States</option>
                          <option value="cn" style={{ background: '#0E1F38' }}>China</option>
                          <option value="tr" style={{ background: '#0E1F38' }}>Turkey</option>
                          <option value="gr" style={{ background: '#0E1F38' }}>Greece</option>
                          <option value="ro" style={{ background: '#0E1F38' }}>Romania</option>
                          <option value="other" style={{ background: '#0E1F38' }}>Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Shipment Type */}
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body font-semibold uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>
                        Primary Shipment Type
                      </label>
                      <select
                        name="shipmentType"
                        value={form.shipmentType}
                        onChange={handleChange}
                        className="rounded-xl px-4 py-2.5 text-sm font-body outline-none transition-all duration-200 appearance-none"
                        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: form.shipmentType ? '#FFFFFF' : '#7A9AB8' }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      >
                        <option value="" style={{ background: '#0E1F38' }}>Select type (optional)</option>
                        <option value="sea-fcl" style={{ background: '#0E1F38' }}>Sea Freight — FCL</option>
                        <option value="sea-lcl" style={{ background: '#0E1F38' }}>Sea Freight — LCL</option>
                        <option value="air" style={{ background: '#0E1F38' }}>Air Freight</option>
                        <option value="inland" style={{ background: '#0E1F38' }}>Inland / Road</option>
                        <option value="customs" style={{ background: '#0E1F38' }}>Customs Brokerage</option>
                        <option value="multimodal" style={{ background: '#0E1F38' }}>Multimodal</option>
                      </select>
                    </div>

                    {/* Error */}
                    {error && (
                      <div className="px-4 py-3 rounded-xl" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)' }}>
                        <p className="text-xs font-body" style={{ color: '#FCA5A5' }}>{error}</p>
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="mt-1 inline-flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl text-sm font-semibold font-body transition-all duration-200 hover:opacity-90 hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ background: 'linear-gradient(135deg, #1B6B8A 0%, #134E68 100%)', color: '#fff', boxShadow: '0 4px 20px rgba(27,107,138,0.35)' }}
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                          Submitting…
                        </>
                      ) : (
                        <>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 7a2 2 0 0 1 2 2m4 0a6 6 0 0 1-7.743 5.743L11 17H9v2H7v2H4a1 1 0 0 1-1-1v-2.586a1 1 0 0 1 .293-.707l5.964-5.964A6 6 0 1 1 21 9z" />
                          </svg>
                          Request MY WTO Access
                        </>
                      )}
                    </button>

                    <p className="text-center font-body leading-relaxed" style={{ fontSize: '11px', color: '#7A9AB8' }}>
                      Approved within 1 business day · Secure &amp; Encrypted
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer strip */}
      <div className="w-full px-6 py-4 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-xs font-body" style={{ color: '#7A9AB8' }}>
          World Transport Overseas &bull;{' '}
          <a href="mailto:info@wto.com" className="underline underline-offset-2" style={{ color: '#7EC8DC' }}>info@wto.com</a>
        </p>
      </div>
    </main>
  );
}
